import { z } from 'zod';
import { hexColorSchema, localeSchema } from './common.js';
/**
 * Organization, Brand, Location and Staff shapes — API-CONTRACT.md §3 (v1).
 *
 * Vocabulary follows PROJECT-OVERVIEW.md §7 exactly. No `merchant`, `store`,
 * `shop`, `tenant` or `client` where one of these terms applies.
 */
export const updateOrganizationSchema = z.strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    countryCode: z.string().trim().length(2).toUpperCase().optional(),
    defaultCurrency: z.string().trim().length(3).toUpperCase().optional(),
});
export const createBrandSchema = z.strictObject({
    name: z.string().trim().min(1).max(120),
    slug: z
        .string()
        .trim()
        .toLowerCase()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens')
        .optional(),
    primaryColor: hexColorSchema.optional(),
    description: z.string().trim().max(500).nullable().optional(),
    logoUrl: z.url().max(500).nullable().optional(),
});
export const updateBrandSchema = createBrandSchema.partial();
/**
 * Image types a brand logo may be — DATA-MODEL.md's `Brand.logoUrl`.
 *
 * A closed list, not a prefix match on `image/`. `image/svg+xml` is an XML
 * document that runs script when opened at its own URL, and logos are served
 * from a domain the platform controls: an SVG upload would hand every merchant
 * a stored-XSS primitive.
 */
export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** A logo is a logo. Anything larger is a mistake, or an attempt to fill a bucket. */
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;
/**
 * Asking for somewhere to put a logo.
 *
 * The size is declared up front and signed into the URL, so the presign cannot
 * become a blank cheque for a four-gigabyte upload.
 */
export const logoUploadRequestSchema = z.strictObject({
    contentType: z.enum(LOGO_CONTENT_TYPES),
    contentLength: z.number().int().positive().max(MAX_LOGO_BYTES),
});
/**
 * IANA timezone. Validated against the runtime's own database rather than a
 * hard-coded list — a stale allowlist silently rejects a real zone, and
 * `Intl.supportedValuesOf` is the same source `Intl.DateTimeFormat` will use to
 * render every date in the dashboard.
 */
export const timezoneSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Not a recognised timezone, for example Asia/Beirut' });
const locationFields = {
    name: z.string().trim().min(1).max(120),
    address: z.string().trim().max(300).nullable().optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
    timezone: timezoneSchema,
};
export const createLocationSchema = z.strictObject({
    ...locationFields,
    timezone: timezoneSchema.default('Asia/Beirut'),
});
/**
 * Built from the fields rather than `createLocationSchema.partial()`: Zod 4
 * keeps an inner `.default()` under `.partial()`, so a PATCH that left
 * `timezone` out would quietly reset it to the default.
 */
export const updateLocationSchema = z.strictObject(locationFields).partial();
export const createStaffSchema = z.strictObject({
    fullName: z.string().trim().min(1).max(120),
    pin: z.string().regex(/^\d{4}$/, 'The PIN is four digits'),
    locationIds: z.array(z.uuid()).min(1, 'Assign at least one location'),
    /**
     * The counter language for this person — features/I18N.md.
     *
     * Per staff member, not per device: a tablet is shared across a shift, and two
     * baristas on it may not read the same language. features/I18N.md calls this the priority,
     * because "baristas are the least likely to be comfortable in English, and
     * they are the single point of failure for adoption."
     */
    locale: localeSchema.optional(),
});
export const updateStaffSchema = z.strictObject({
    fullName: z.string().trim().min(1).max(120).optional(),
    isActive: z.boolean().optional(),
    locationIds: z.array(z.uuid()).min(1).optional(),
    locale: localeSchema.optional(),
});
export const resetPinSchema = z.strictObject({
    pin: z.string().regex(/^\d{4}$/, 'The PIN is four digits'),
});
export const updateUserProfileSchema = z.strictObject({
    fullName: z.string().trim().min(1).max(120).optional(),
    locale: localeSchema.optional(),
});
//# sourceMappingURL=tenancy.js.map